import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Chip,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import userProfileEnums from '../data/userProfileEnums.json';

// Shared look (design tokens) - same as the rest of IZHub
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
  },
};

const Skill = ({ selectedSkills, setSelectedSkills }) => {
  const [inputValue, setInputValue] = useState('');

  const filteredSkills = inputValue
    ? userProfileEnums.skills.filter(
        (skill) =>
          skill.toLowerCase().includes(inputValue.toLowerCase()) &&
          !selectedSkills.includes(skill)
      )
    : [];

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSkillClick = (skill) => {
    if (!selectedSkills.includes(skill)) {
      setSelectedSkills([...selectedSkills, skill]);
      setInputValue('');
    }
  };

  const removeSkill = (skillToRemove) => {
    setSelectedSkills(selectedSkills.filter((skill) => skill !== skillToRemove));
  };

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: '16px',
        border: `1px solid ${ui.border}`,
        backgroundColor: '#fff',
        boxShadow: ui.shadow,
        p: { xs: 2.5, sm: 3.5 },
      }}
    >
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            backgroundColor: ui.primarySoft,
            color: ui.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PsychologyOutlinedIcon fontSize="small" />
        </Box>
        <Typography variant="h6" sx={{ color: ui.text, fontWeight: 'bold', flexGrow: 1 }}>
          SKILLS
        </Typography>
        <Chip
          size="small"
          label={`${selectedSkills.length} selected`}
          sx={{ fontWeight: 700, borderRadius: '8px', backgroundColor: ui.primarySoft, color: ui.primaryDark }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ position: 'relative' }}>
          <TextField
            fullWidth
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Type to search skills..."
            variant="outlined"
            sx={fieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: ui.muted }} />
                </InputAdornment>
              ),
            }}
          />
          {filteredSkills.length > 0 && inputValue && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                mt: 1,
                zIndex: 10,
                maxHeight: 260,
                overflowY: 'auto',
                borderRadius: '12px',
                border: `1px solid ${ui.border}`,
              }}
            >
              {filteredSkills.map((skill) => (
                <Box
                  key={skill}
                  onClick={() => handleSkillClick(skill)}
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    bgcolor: 'white',
                    '&:hover': { bgcolor: ui.primarySoft },
                    cursor: 'pointer',
                    borderBottom: `1px solid ${ui.border}`,
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Typography variant="body1">{skill}</Typography>
                </Box>
              ))}
            </Paper>
          )}
        </Box>

        {selectedSkills.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 3,
              px: 2,
              borderRadius: '12px',
              border: `1px dashed ${ui.border}`,
              backgroundColor: ui.surface,
            }}
          >
            <Typography variant="body2" sx={{ color: ui.muted }}>
              No skills added yet. Search above to add one.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedSkills.map((skill) => (
              <Chip
                key={skill}
                label={skill}
                onDelete={() => removeSkill(skill)}
                deleteIcon={<CloseIcon />}
                sx={{
                  bgcolor: ui.primarySoft,
                  color: ui.primaryDark,
                  fontWeight: 600,
                  borderRadius: '10px',
                  border: '1px solid #bfe6ee',
                  '&:hover': { bgcolor: '#d3eff5' },
                  '& .MuiChip-deleteIcon': {
                    color: ui.primaryDark,
                    '&:hover': { color: '#d32f2f' },
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Skill;